import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import {
  BadgeOutlined as BadgeOutlinedIcon,
  CloseRounded as CloseRoundedIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FolderShared as FolderSharedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  SecurityOutlined as SecurityOutlinedIcon,
  StorefrontOutlined as StorefrontOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserSchema } from '../../types/auth.type';
import {
  OrganizationSchema,
  OrganizationType,
  getOrganizationTypeLabel,
  isSiteOperationsOrgType,
} from '../../types/organization.type';
import { inferRoleLabel, type RoleOrgType } from '../../auth';
import { permissionSummaryLabel } from '../../utils/permissions.helpers';
import OrgPermissionsBreakdown from './OrgPermissionsBreakdown';
import {
  buildSiteIdToLabelMap,
  formatSitesListCell,
} from '../../utils/users.helpers';
import { getCollectionSites } from '../../services/collection-site.service';
import { CollectionSiteSchema } from '../../types/collection-site.type';
import PageTitleIcon from '../PageTitleIcon';
import { CONTENT_FONT_FAMILY_VAR } from '../../constants/fonts';

interface UserDetailsDialogProps {
  open: boolean;
  user: UserSchema | null;
  organization: OrganizationSchema | null;
  roleOrgType: RoleOrgType | null;
  canWrite: boolean;
  onClose: () => void;
  onEdit: (user: UserSchema) => void;
}

function getUserId(u: UserSchema): string {
  return String(u.sub ?? u.id ?? '');
}

function displayValue(value?: string | null) {
  return value?.trim() ? value : '—';
}

function formatDateTime(iso?: string | null) {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatDateOnly(iso?: string | null) {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function displayName(user: UserSchema): string {
  const fromParts = [user.data?.first_name, user.data?.last_name].filter(Boolean).join(' ');
  return fromParts || user.email || user.username || 'Employee';
}

function initials(user: UserSchema): string {
  const name = displayName(user);
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0]?.[0] ?? user.username?.[0] ?? '?').toUpperCase();
}

function FieldItem({
  label,
  value,
  children,
  fullWidth,
  columns = 'half',
  copyable,
}: {
  label: string;
  value?: string | null;
  children?: React.ReactNode;
  fullWidth?: boolean;
  columns?: 'half' | 'third' | 'full';
  copyable?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const gridSize = (() => {
    if (fullWidth || columns === 'full') return { xs: 12 };
    if (columns === 'third') return { xs: 12, md: 4 };
    return { xs: 12, sm: 6 };
  })();

  const text = value?.trim() ?? '';
  const canCopy = Boolean(copyable && text);

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const defaultValue = canCopy ? (
    <Stack direction="row" alignItems="flex-start" spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
        {text}
      </Typography>
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <IconButton
          size="small"
          onClick={() => void handleCopy()}
          aria-label={`Copy ${label}`}
          sx={{ flexShrink: 0, color: 'text.secondary', p: 0.25, mt: -0.25 }}
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : (
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {displayValue(value)}
    </Typography>
  );

  return (
    <Grid item {...gridSize}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children ?? defaultValue}</Box>
    </Grid>
  );
}

function DetailSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: isDark
          ? `0 8px 24px ${alpha('#000', 0.28)}`
          : `0 8px 24px ${alpha('#0f172a', 0.05)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            mb: 2,
            pb: 1.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontFamily: CONTENT_FONT_FAMILY_VAR,
              fontWeight: 700,
              fontSize: '1rem',
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function getOrganizationTypeColor(type: OrganizationType, theme: Theme): string {
  switch (type) {
    case OrganizationType.RECYCLER:
      return theme.palette.success.main;
    case OrganizationType.WASTE_OUTLET:
      return theme.palette.primary.main;
    case OrganizationType.TRUCK:
      return theme.palette.warning.main;
    case OrganizationType.CHARITY:
      return theme.palette.info.main;
    default:
      return theme.palette.grey[500];
  }
}

const UserDetailsDialog: React.FC<UserDetailsDialogProps> = ({
  open,
  user,
  organization,
  roleOrgType,
  canWrite,
  onClose,
  onEdit,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [siteIdToLabel, setSiteIdToLabel] = useState<Record<string, string>>({});
  const [sitesLoading, setSitesLoading] = useState(false);

  const orgId = organization?.id;
  const orgType = organization?.organization_type;
  const isSiteOpsOrg = orgType ? isSiteOperationsOrgType(orgType) : false;
  const isTruckOrg = orgType === OrganizationType.TRUCK;

  useEffect(() => {
    if (!open || !orgId || !isSiteOpsOrg) {
      setSiteIdToLabel({});
      setSitesLoading(false);
      return;
    }

    let cancelled = false;
    setSitesLoading(true);

    void (async () => {
      try {
        const res = (await getCollectionSites({
          organization_id: orgId,
          page: 1,
          limit: 500,
        })) as { result?: CollectionSiteSchema[] };
        const sites = res?.result ?? [];
        if (!cancelled) setSiteIdToLabel(buildSiteIdToLabelMap(sites));
      } catch {
        if (!cancelled) setSiteIdToLabel({});
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, orgId, isSiteOpsOrg]);

  const handleEdit = () => {
    if (!user) return;
    onClose();
    onEdit(user);
  };

  if (!user || !organization || !orgId || !roleOrgType || !orgType) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.12)}`,
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            position: 'relative',
            px: { xs: 2.5, sm: 3 },
            pt: 2.5,
            pb: 2,
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            background:
              theme.palette.mode === 'dark'
                ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, transparent 100%)`
                : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1)}`,
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={t('common.close')}
            sx={{
              position: 'absolute',
              top: 12,
              insetInlineEnd: 12,
              color: 'text.secondary',
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 5 }}>
            <PageTitleIcon icon={<PersonOutlineIcon />} size={48} />
            <Typography
              component="h2"
              sx={{
                fontFamily: CONTENT_FONT_FAMILY_VAR,
                fontWeight: 800,
                fontSize: '1.25rem',
                letterSpacing: '-0.02em',
              }}
            >
              {t('users.detailsTitle')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">{t('users.detailsEmpty')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const userId = getUserId(user);
  const isActive = (user.status ?? 'active') !== 'inactive';
  const roleLabel = inferRoleLabel(user, orgId, roleOrgType);
  const accessSummary = permissionSummaryLabel(user, orgId, orgType);
  const sitesEntry = user.data?.sites?.[orgId];
  const sitesCell = formatSitesListCell(sitesEntry, siteIdToLabel);

  const hasDriverFields = Boolean(
    user.data?.driver_license_number?.trim() ||
      user.data?.vehicle_registration_number?.trim() ||
      user.data?.driver_license_expiry?.trim(),
  );
  const showDriverSection = hasDriverFields || isTruckOrg;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.12)}`,
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 24px 64px ${alpha('#000', 0.55)}`
              : `0 20px 50px ${alpha('#0f172a', 0.16)}`,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          position: 'relative',
          px: { xs: 2.5, sm: 3 },
          pt: 2.75,
          pb: 2.25,
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit',
          letterSpacing: 'inherit',
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, transparent 100%)`
              : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1)}`,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('common.close')}
          sx={{
            position: 'absolute',
            top: 12,
            insetInlineEnd: 12,
            color: 'text.secondary',
            bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.2 : 0.04),
            '&:hover': {
              bgcolor: alpha(
                theme.palette.common.black,
                theme.palette.mode === 'dark' ? 0.32 : 0.08,
              ),
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 5 }}>
          <PageTitleIcon icon={<PersonOutlineIcon />} size={48} />
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: CONTENT_FONT_FAMILY_VAR,
                fontWeight: 800,
                fontSize: { xs: '1.2rem', sm: '1.35rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                color: 'text.primary',
              }}
            >
              {t('users.detailsTitle')}
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: CONTENT_FONT_FAMILY_VAR,
                mt: 0.5,
                fontWeight: 500,
                fontSize: '0.875rem',
                lineHeight: 1.45,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName(user)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers={false} sx={{ pt: 2.5, pb: 2, px: { xs: 2.5, sm: 3 } }}>
        {sitesLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Loading collection site names…
          </Typography>
        )}

        <Stack spacing={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05),
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: isActive ? 'primary.main' : 'grey.500',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                }}
              >
                {initials(user)}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Typography
                  sx={{
                    fontFamily: CONTENT_FONT_FAMILY_VAR,
                    fontWeight: 800,
                    fontSize: '1.15rem',
                    lineHeight: 1.25,
                  }}
                  noWrap
                  title={displayName(user)}
                >
                  {displayName(user)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: CONTENT_FONT_FAMILY_VAR,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                  noWrap
                  title={user.username}
                >
                  {user.username}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label={isActive ? t('users.form.active') : t('users.form.inactive')}
                    color={isActive ? 'success' : 'default'}
                    sx={{ borderRadius: '8px', fontWeight: 600 }}
                  />
                  <Chip
                    size="small"
                    label={getOrganizationTypeLabel(orgType)}
                    sx={{
                      bgcolor: getOrganizationTypeColor(orgType, theme),
                      color: theme.palette.getContrastText(
                        getOrganizationTypeColor(orgType, theme),
                      ),
                      fontWeight: 600,
                      borderRadius: '8px',
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          <DetailSection icon={<PersonOutlineIcon fontSize="small" />} title="Account">
            <Grid container spacing={2}>
              <FieldItem label="Email" value={user.username} copyable />
              <FieldItem label="Phone" value={user.phone} copyable />
              <FieldItem label="User ID" value={userId} copyable columns="full" />
            </Grid>
          </DetailSection>

          <DetailSection icon={<BadgeOutlinedIcon fontSize="small" />} title="Personal details">
            <Grid container spacing={2}>
              <FieldItem label="First name" value={user.data?.first_name} />
              <FieldItem label="Last name" value={user.data?.last_name} />
              <FieldItem label="Gender" value={user.data?.gender} />
              <FieldItem label="Nationality" value={user.data?.nationality} />
              <FieldItem label="Date of birth" value={formatDateOnly(user.data?.date_of_birth)} />
              <FieldItem label="Employee level" value={user.data?.employee_level ?? null} />
            </Grid>
          </DetailSection>

          <DetailSection icon={<SecurityOutlinedIcon fontSize="small" />} title="Role & access">
            <Stack spacing={1.5}>
              <Grid container spacing={2}>
                <FieldItem label="Organization" value={organization.name} />
                <FieldItem label="Role" value={roleLabel} />
                <FieldItem label="Access summary" value={accessSummary} columns="full" />
              </Grid>

              {isSiteOpsOrg && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <StorefrontOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Tooltip title={sitesCell.full}>
                    <Typography variant="body2" color="text.secondary">
                      Collection sites: {sitesCell.display}
                    </Typography>
                  </Tooltip>
                </Stack>
              )}

              <Divider />
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                Capabilities
              </Typography>
              <OrgPermissionsBreakdown
                user={user}
                orgId={orgId}
                orgType={roleOrgType}
                compact
              />
            </Stack>
          </DetailSection>

          {showDriverSection && (
            <DetailSection
              icon={<LocalShippingOutlinedIcon fontSize="small" />}
              title="Driver & vehicle"
            >
              <Grid container spacing={2}>
                <FieldItem
                  label="License number"
                  value={user.data?.driver_license_number}
                  copyable
                />
                <FieldItem
                  label="License expiry"
                  value={formatDateOnly(user.data?.driver_license_expiry)}
                />
                <FieldItem
                  label="Vehicle registration"
                  value={user.data?.vehicle_registration_number}
                  copyable
                  columns="full"
                />
              </Grid>
            </DetailSection>
          )}

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FolderSharedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Record
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
              <Grid container spacing={2}>
                <FieldItem label="Created" value={formatDateTime(user.created_at)} />
                <FieldItem label="Updated" value={formatDateTime(user.updated_at)} />
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(
            theme.palette.primary.main,
            theme.palette.mode === 'dark' ? 0.06 : 0.03,
          ),
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}
        >
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          disabled={!canWrite}
          onClick={handleEdit}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          {t('common.edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsDialog;
